import React from "react";
import ReactDOM from "react-dom";
import { type Store } from "redux";
import { create } from "jss";
import { jssPreset } from "@material-ui/core/styles";

import jsPDF from "jspdf";
import html2canvas, { type Options as HTML2CanvasOptions } from "html2canvas";

import Providers from "src/Providers";
import MyResellerGenieLogo from "src/assets/svgs/logo.svg";
const FooterText = "Created with";
const PrintFooterHeight = "24px";

function ptToMm(points: number): number {
  return points * 0.352778;
}

interface RenderOptions {
  footer?: boolean;
  fit?: boolean;
  scale?: number;
  filename?: string;
  onclone?: HTML2CanvasOptions["onclone"];
  windowWidth?: HTML2CanvasOptions["windowWidth"];
}

export async function render(
  element: HTMLElement,
  {
    footer = false,
    fit = false,
    scale = 2,
    filename = "report.pdf",
    onclone,
    windowWidth = 1200,
  }: RenderOptions = {}
) {
  const opts: Partial<HTML2CanvasOptions> = {
    // scale,
    useCORS: true,
    logging: false,
    onclone,
  };

  if (windowWidth) opts.windowWidth = windowWidth;
  const canvas = await html2canvas(element, opts);

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const footerHeight = 12;
  const paddingBottom = 2;

  const drawFooter = async () => {
    const svg = await new Promise<HTMLCanvasElement>((rs, rj) => {
      const img = new Image();
      img.addEventListener("load", () => {
        const aspectRatio = img.width / img.height;
        const canvas = document.createElement("canvas");
        canvas.width = 96 * aspectRatio;
        canvas.height = 96;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          rj(new Error("No canvas context"));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        rs(canvas);
      });
      img.addEventListener("error", (e) => rj(e));

      if (MyResellerGenieLogo.trim().startsWith("<svg")) {
        img.src = `data:image/svg+xml;base64,${btoa(
          unescape(encodeURIComponent(MyResellerGenieLogo))
        )}`;
      } else {
        img.src = MyResellerGenieLogo;
      }
    });

    const height = footerHeight;
    const width = (svg.width / svg.height) * height;
    pdf.setFontSize(10);
    pdf.text(
      FooterText,
      pageWidth - 2 - width - 2 - pdf.getTextWidth(FooterText),
      pageHeight - paddingBottom - height / 2 + ptToMm(10) / 2
    );
    pdf.addImage(
      svg,
      "PNG",
      pageWidth - 2 - width,
      pageHeight - footerHeight - paddingBottom,
      width,
      height
    );
  };

  if (fit) {
    const scaleFactor = Math.min(
      pageWidth / canvas.width,
      (pageHeight - footerHeight - paddingBottom) / canvas.height
    );

    pdf.addImage(
      imgData,
      "PNG",
      0,
      0,
      canvas.width * scaleFactor,
      canvas.height * scaleFactor
    );

    await drawFooter();
  } else {
    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pageWidth) / imgProps.width;

    let heightLeft = imgHeight;
    let position = 0;

    while (heightLeft > 0) {
      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      await drawFooter();
      heightLeft -= pageHeight;
      position -= pageHeight;

      if (heightLeft > 0) pdf.addPage();
    }
  }

  await pdf.save(filename);
}

interface PrintOptions {
  store: Store;
  footer?: boolean;
  title?: string;
  width?: string;
  debug?: boolean;
  orientation?: "portrait" | "landscape";
  rotate?: boolean;
}

function getLogoDataUrl(): string {
  if (MyResellerGenieLogo.trim().startsWith("<svg")) {
    return `data:image/svg+xml;base64,${btoa(
      unescape(encodeURIComponent(MyResellerGenieLogo))
    )}`;
  }
  return MyResellerGenieLogo;
}

/**
 * Copies all stylesheets from the parent document into the target document,
 * and returns a promise that resolves when all external <link> stylesheets
 * have finished loading.
 */
function copyStylesheets(targetDoc: Document): Promise<void[]> {
  const styleSheets = Array.from(document.styleSheets);
  const styleContent: string[] = [];
  const linkLoadPromises: Promise<void>[] = [];

  for (const sheet of styleSheets) {
    try {
      if (sheet.href) {
        const link = targetDoc.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        linkLoadPromises.push(
          new Promise<void>((resolve) => {
            link.addEventListener("load", () => resolve(), { once: true });
            link.addEventListener("error", () => resolve(), { once: true });
          })
        );
        targetDoc.head.appendChild(link);
      } else if (sheet.cssRules) {
        const rules = Array.from(sheet.cssRules)
          .map((r) => r.cssText)
          .join("\n");
        styleContent.push(rules);
      }
    } catch {
      if (sheet.href) {
        const link = targetDoc.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        linkLoadPromises.push(
          new Promise<void>((resolve) => {
            link.addEventListener("load", () => resolve(), { once: true });
            link.addEventListener("error", () => resolve(), { once: true });
          })
        );
        targetDoc.head.appendChild(link);
      }
    }
  }

  if (styleContent.length > 0) {
    const style = targetDoc.createElement("style");
    style.textContent = styleContent.join("\n");
    targetDoc.head.appendChild(style);
  }

  return Promise.all(linkLoadPromises);
}

/**
 * Adds print-specific CSS to the target document:
 */
function addPrintStyles(
  targetDoc: Document,
  {
    footer,
    orientation,
    rotate,
    width,
  }: {
    footer: boolean;
    orientation?: "portrait" | "landscape";
    rotate?: boolean;
    width?: string;
  }
): void {
  const printStyle = targetDoc.createElement("style");
  printStyle.textContent = `
    @media print {
      @page {
        margin: 0;
        padding: 0;
        ${orientation ? `size: ${orientation};` : ""}
      }

    }

    body {
      margin: 0;
      padding: 0;
    }

    .render-print-wrapper {
      width: 100%;
      border-collapse: collapse;
      ${
        rotate
          ? `transform: rotate(90deg) translate(0%, -${
              width || "100%"
            }); transform-origin: top left;`
          : ""
      }
    }

    .render-print-footer-wrapper {
      display: table-footer-group;
    }

    .render-print-footer {
      text-align: right;
      vertical-align: bottom;
    }

    .render-print-footer div {
      height: ${PrintFooterHeight};
      font-size: 10px;
      font-family: Arial, sans-serif;
      display: flex;
      justify-content: flex-end;
      gap: 4px;
      margin: 0 4px 0 0;
    }

    .render-print-footer img {
      height: 100%;
    }
  `;
  targetDoc.head.appendChild(printStyle);
}

/**
 * Waits for all <img> elements to finish loading, including images inserted
 * after the initial render (e.g. async React updates).
 */
function waitForImages(
  targetDoc: Document,
  { idleMs = 200, timeoutMs = 15000 } = {}
): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const pendingLoads = new Set<HTMLImageElement>();
    let quietTimer: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      if (quietTimer) clearTimeout(quietTimer);
      clearTimeout(maxTimer);
      resolve();
    };

    const trackImage = (img: HTMLImageElement) => {
      if (pendingLoads.has(img) || img.complete) return;
      pendingLoads.add(img);
      const onSettled = () => {
        pendingLoads.delete(img);
        scheduleFinish();
      };
      img.addEventListener("load", onSettled, { once: true });
      img.addEventListener("error", onSettled, { once: true });
    };

    const scan = () => {
      targetDoc.querySelectorAll("img").forEach((img) => trackImage(img));
    };

    const scheduleFinish = () => {
      scan();
      if (pendingLoads.size > 0) return;
      if (quietTimer) clearTimeout(quietTimer);
      quietTimer = setTimeout(() => {
        scan();
        if (pendingLoads.size === 0) finish();
      }, idleMs);
    };

    const observer = new MutationObserver(() => scheduleFinish());
    observer.observe(targetDoc.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["src"],
    });

    scheduleFinish();
    const maxTimer = setTimeout(finish, timeoutMs);
  });
}

export async function printReport(
  content: React.ReactElement,
  {
    store,
    footer = true,
    title = "Report",
    width = "960px",
    debug = false,
    orientation,
    rotate,
  }: PrintOptions
): Promise<void> {
  const iframe = document.createElement("iframe");

  if (debug) {
    Object.assign(iframe.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      border: "2px solid red",
      zIndex: "99999",
      background: "white",
    });
  } else {
    Object.assign(iframe.style, {
      position: "absolute",
      top: "-10000px",
      left: "-10000px",
      width: 0,
      height: 0,
      border: "none",
    });
  }

  document.body.appendChild(iframe);

  // Wait for the iframe to load the blank document
  await new Promise<void>((resolve) => {
    iframe.addEventListener("load", () => resolve(), { once: true });
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write("<!DOCTYPE html><html><head></head><body></body></html>");
      iframeDoc.close();
    }
  });

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  const iframeWindow = iframe.contentWindow;

  if (!iframeDoc || !iframeWindow) {
    document.body.removeChild(iframe);
    return;
  }

  // Set the document title – browsers use this as the suggested filename
  // when saving as PDF
  iframeDoc.title = title;

  // Copy parent stylesheets and add print-specific styles
  const stylesheetsLoaded = copyStylesheets(iframeDoc);
  addPrintStyles(iframeDoc, { footer, orientation, rotate, width });

  // Create a JSS instance that injects styles into the iframe's <head>
  const jss = create({
    ...jssPreset(),
    insertionPoint: iframeDoc.head,
  });

  // Create a mount point inside the iframe
  const mountPoint = iframeDoc.createElement("div");
  iframeDoc.body.appendChild(mountPoint);

  // Render the React component into the iframe with all app providers + JSS targeting the iframe
  const logoSrc = getLogoDataUrl();
  await new Promise<void>((resolve) => {
    ReactDOM.render(
      <Providers store={store} jss={jss} disableSalesTransactionsDataLoader>
        <div style={{ width }}>
          {footer ? (
            <table className="render-print-wrapper">
              <tbody>
                <tr>
                  <td>{content}</td>
                </tr>
              </tbody>
              <tfoot className="render-print-footer-wrapper">
                <tr>
                  <td className="render-print-footer">
                    <div>
                      {FooterText}
                      <img src={logoSrc} alt="My Reseller Genie" />
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            content
          )}
        </div>
      </Providers>,
      mountPoint,
      () => resolve()
    );
  });

  // Wait for all external stylesheets to finish loading
  await stylesheetsLoaded;

  await waitForImages(iframeDoc);

  if (debug) return;

  // Print and wait for the print dialog to close
  await new Promise<void>((resolve) => {
    iframeWindow.addEventListener("afterprint", () => resolve(), {
      once: true,
    });
    const savedTitle = document.title;
    document.title = title;
    iframeWindow.focus();
    iframeWindow.print();
    document.title = savedTitle;
  });

  // Clean up: unmount React tree and remove iframe
  ReactDOM.unmountComponentAtNode(mountPoint);
  if (iframe.parentNode) {
    document.body.removeChild(iframe);
  }
}
